/**
 * Frontend Error Logging Types
 *
 * Type definitions for the client-side error logging system.
 * These types match the backend error logging types to ensure
 * consistent error capture and processing across the stack.
 *
 * @module lib/datahub/errorLogging/types
 *
 * @example
 * ```typescript
 * import type { ErrorSeverity, ErrorPayload, Breadcrumb } from './types';
 *
 * const payload: ErrorPayload = {
 *   message: 'Cannot read property of undefined',
 *   name: 'TypeError',
 *   severity: 'error',
 *   category: 'uncaught_exception',
 *   source: 'client',
 *   capturePoint: 'window_onerror',
 *   breadcrumbs: [],
 *   sessionId: 'session-123',
 *   environment: 'production'
 * };
 * ```
 */

export type ErrorSeverity = 'critical' | 'error' | 'warning' | 'info';

export type ErrorCategory =
  | 'react_render'
  | 'unhandled_promise'
  | 'uncaught_exception'
  | 'network_request'
  | 'firebase_auth'
  | 'firebase_firestore'
  | 'firebase_functions'
  | 'sync_error'
  | 'validation_error'
  | 'console_error'
  | 'unknown';

export type CapturePoint =
  | 'error_boundary'
  | 'window_onerror'
  | 'unhandled_rejection'
  | 'network_interceptor'
  | 'try_catch'
  | 'console_intercept'
  | 'manual';

export interface Breadcrumb {
  timestamp: number;
  type: 'navigation' | 'click' | 'api_call' | 'api_response' | 'state_change' | 'console';
  category: string;
  message: string;
  data?: Record<string, unknown>;
}

export interface CaptureErrorInput {
  error: Error | unknown;
  severity?: ErrorSeverity;
  category?: ErrorCategory;
  capturePoint: CapturePoint;
  componentName?: string;
  additionalContext?: Record<string, unknown>;
  /**
   * Explicit Sentry grouping key, for the callers where frame-based grouping is
   * structurally wrong.
   *
   * Sentry groups by stack frames. That is right when the stack is the throw
   * site and useless when it is not: the network interceptor SYNTHESISES its
   * Error at one line, so every failing endpoint in the app produces the same
   * frames and lands in ONE issue. Resolving that issue "resolves" failures from
   * endpoints nobody looked at.
   *
   * Set it only when you can name why the default is wrong. It is a first-class
   * typed field rather than a key inside `additionalContext` on purpose —
   * `additionalContext` is forwarded verbatim into both Sentry's `extra` and the
   * `error_events` payload, so an internal grouping hint hidden there would leak
   * into two stored records as if it were error data.
   */
  fingerprint?: readonly string[];
  /**
   * Extra Sentry tags for this event — indexed, filterable, and shown as a
   * distribution on the issue page.
   *
   * This is the COMPANION to `fingerprint`, and the pair is what makes a
   * deliberately-coarse grouping safe. Whatever a caller removes from the
   * grouping key it should add back here: the fingerprint decides what counts
   * as the SAME failure, a tag preserves the dimensions WITHIN it. The network
   * interceptor groups status-0 transport failures by host rather than by
   * endpoint, and tags the endpoint — so one host-level noise bucket still
   * shows which endpoints it was made of, instead of becoming 39 issues.
   *
   * Values must be low-enough cardinality to be worth indexing (an endpoint,
   * a status, a feature name) and must never carry per-user or per-document
   * identifiers. Tags are issue-page-visible, the same trust boundary as
   * `fingerprint`.
   *
   * `category`, `capturePoint` and `source` are set by captureError and
   * deliberately WIN over anything passed here — a caller cannot relabel the
   * three tags the triage UI and every saved search depend on.
   */
  tags?: Readonly<Record<string, string>>;
  /**
   * Deliver to Sentry but NOT to the `error_groups` / `error_events` backend.
   *
   * The two sinks are not copies of each other and never have been. Sentry
   * groups an entire class into one issue with a tag distribution (status-0
   * transport failures are bucketed by HOST, with a `network_endpoint` tag —
   * see networkFingerprint). `error_groups` fingerprints per message, so the
   * same class arrives as one document PER CALLABLE, each needing its own
   * human dismissal. Measured 2026-09-21: 24 such groups in 30 days, 18
   * callables, not one of them a defect.
   *
   * So this flag says "this class is already aggregated where it belongs; do
   * not also mint a triage document for it". It is NOT a mute button:
   *   - the event still takes the full Sentry path — this flag skips only the
   *     backend POST, and nothing else;
   *   - the drop is COUNTED (transportFailureCounter), and a burst escalates
   *     into a normal report that DOES create an error group.
   *
   * ⚠️ ONE HONEST CAVEAT, and it is pre-existing rather than introduced here:
   * `captureError` returns early — before Sentry — while another call is
   * awaiting its backend round trip (`isSendingError`). So "takes the full
   * Sentry path" means "is not skipped BY THIS FLAG", not "is guaranteed to
   * arrive". That gate is a known, separately-tracked defect (see
   * __tests__/errorIdentity.test.ts, ERROR-PIPELINE-DROP-VISIBILITY) and it
   * bites hardest during exactly the burst this flag's caller exists to
   * detect. Do not read this comment as a delivery guarantee; nothing in this
   * pipeline offers one.
   *
   * 🔴 Set it only where BOTH of those are true. A caller that sets this
   * without a counter behind it has built a silent drop, which is the failure
   * every other suppression in this module is written to avoid.
   */
  skipErrorGroups?: boolean;
}

export interface ErrorPayload {
  message: string;
  name: string;
  stack?: string;
  code?: string;
  severity: ErrorSeverity;
  category: ErrorCategory;
  source: 'client';
  capturePoint: CapturePoint;
  file?: string;
  line?: number;
  column?: number;
  functionName?: string;
  componentName?: string;
  userId?: string;
  userEmail?: string;
  sessionId: string;
  correlationId?: string;
  currentRoute?: string;
  connectionId?: string;
  entityType?: string;
  breadcrumbs: Breadcrumb[];
  environment: 'production' | 'development';
  browser?: string;
  deviceType?: 'desktop' | 'mobile' | 'tablet';
  appVersion?: string;
  userAgent?: string;
  additionalContext?: Record<string, unknown>;
}

export interface ErrorLoggingConfig {
  /** Whether error logging is enabled */
  enabled: boolean;
  /** Console log errors in development */
  logToConsole: boolean;
  /** Maximum breadcrumbs to keep */
  maxBreadcrumbs: number;
  /** Sampling rate for warnings (0-1) */
  warningSampleRate: number;
  /** Sampling rate for info (0-1) */
  infoSampleRate: number;
}

export const DEFAULT_CONFIG: ErrorLoggingConfig = {
  enabled: true,
  logToConsole: process.env.NODE_ENV !== 'production',
  maxBreadcrumbs: 30,
  warningSampleRate: 0.1, // 10%
  infoSampleRate: 0.01, // 1%
};
